// Added by Sejune Oh on 2019-04-11
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component, createRef } from 'react'
import { RouterProps } from 'react-router'
import { observer } from 'mobx-react'
import { observable, action, makeObservable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Component
import MgmtListTemplate from './MgmtListTemplate'
import StoreLinkedTable from '../StoreLinkedTable'
import { TableUrlQueryProps, TableComponentInitProps, TableOptionProps } from '../../Table'
import Search from '../../Search/'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import { ReactEmptyEvent } from '@/helper/define'

// Util
import { getManagemnetPaths, parseUrlQuery } from '@/helper/util'
import Logger from '@/helper/Logger'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface MgmtListTemplateBaseCProps {
  customButtonComponents?: JSX.Element
  curPathSet?: [string, string]
  hasAdditionalButton?: boolean
  onAdditionalButtonClick?: () => void
  additionalBtnText?: string
}

interface MgmtListTemplateCProps
  extends TableUrlQueryProps,
    MgmtListTemplateBaseCProps,
    TableComponentInitProps,
    RouterProps {
  onAddBtnClick?: ReactEmptyEvent
  refreshMilSec?: number
  hideSearch?: boolean
}

interface TableProps {
  [key: string]: any
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 *  Management List Template Component를 생성합니다.
 *
 * - Generic
 * @IT : Table Interface
 * @II : Item Interface
 *
 * - Parameter
 * @param storeName : List의 테이블에서 참조할 Store 이름.
 */
export default function MgmtListTemplateBuilder<IT = {}, II = {}>(storeName: string, option?: TableOptionProps) {
  const TableComponent = StoreLinkedTable<IT | TableProps, II>({ storeName, option })

  @observer
  class MgmtListTemplateConatiner extends Component<MgmtListTemplateCProps> {
    mounted: boolean = false
    keyword: string = ''

    _basePath: string = ''
    _tableRef = createRef<any>()
    _searchRef = createRef<Search>()
    _interval?: any

    constructor(props: MgmtListTemplateCProps) {
      super(props)
      makeObservable(this, {
        mounted: observable,
        keyword: observable,
        componentDidMount: action,
        setKeyword: action,
      })
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    async componentDidMount() {
      const {
        location: { pathname },
      } = this.props.history
      const { fullPath } = getManagemnetPaths(pathname)

      if (this.props.refreshMilSec) {
        this._interval = setInterval(async () => {
          const tableRef = this._tableRef.current
          Logger.devLog(`${storeName} has been refresehd : ${Date.now()}`)
          tableRef.handleSearch(this.keyword, tableRef.pageIndex)
        }, this.props.refreshMilSec)
      }

      this._basePath = fullPath
      this.mounted = true
    }

    componentWillUnmount() {
      if (this._interval && this.props.refreshMilSec) clearInterval(this._interval)
    }

    setKeyword = (keyword: string) => {
      this.keyword = keyword
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    render() {
      const {
        history,
        headerProps,
        isSortable,
        isSelectable,
        isPaginationable,
        customSetter,
        filter,
        info,
        ...others
      } = this.props
      const parsedQuerySet = this.getQuerySet(this.props.history.location.search)

      return (
        this.mounted && (
          <MgmtListTemplate
            {...others}
            onSearch={this.handleSearch}
            onAddBtnClick={this.handleAddBtnClick}
            defaultSearchKeyword={decodeURI(filter || parsedQuerySet['filter'] || '')}
            searchRef={this._searchRef}
            tableComponent={
              <TableComponent
                ref={this._tableRef}
                history={history}
                basePath={this._basePath}
                headerProps={headerProps}
                isSortable={isSortable}
                isSelectable={isSelectable}
                isPaginationable={isPaginationable}
                updateSearchKeyword={this.updateSearchKeyword}
                customSetter={customSetter}
                info={info}
              />
            }
            customButtonComponents={this.props.customButtonComponents}
            hasAdditionalButton={this.props.hasAdditionalButton}
            onAdditionalButtonClick={this.props.onAdditionalButtonClick}
            additionalBtnText={this.props.additionalBtnText}
          />
        )
      )
    }

    handleSearch = (key: string) => {
      this.setKeyword(key)
      this._tableRef.current.handleSearch(key)
    }

    updateSearchKeyword = (key: string = '') => {
      this.setKeyword(key)
      if (this._searchRef.current) this._searchRef.current.updateSearchKeyword(key)
    }

    handleAddBtnClick = () => {
      if (this.props.onAddBtnClick) {
        this.props.onAddBtnClick()
        return
      }

      this.props.history.push({
        pathname: `${this._basePath}add`,
        state: {
          from: this.props.history.location.pathname,
        },
      })
    }

    getQuerySet = (searchPath?: string) => {
      return (searchPath ? parseUrlQuery(searchPath) : {}) as TableUrlQueryProps
    }
  }

  return MgmtListTemplateConatiner
}
